<template>
  <section class="configure-he3">
    <div class="header">
      <h2>Configurar HE3</h2>
      <p>
        Configure a fase extraordinária (HE3) e o adicional noturno em massa
        para os turnos da empresa. Você pode aplicar também na configuração
        global da empresa.
      </p>
    </div>

    <modal-workshift-detail :workshift="detailWorkshift" :company="companySummary" :timezones="timezones"/>

    <div v-if="loading" class="loading-block">Carregando turnos...</div>

    <div v-if="!loading">
      <!-- Resumo + nuances -->
      <div class="box">
        <label class="box-title">Configuração atual da empresa</label>
        <ul class="summary-list">
          <li>
            <strong>{{ labels.companySettings.extraordinaryPhase }}:</strong>
            {{ helpers.getExtraordinaryPhaseLabel(companySummary.extraordinaryPhase) }}
          </li>
          <li>
            <strong>{{ labels.companySettings.useExtraNight }}:</strong>
            {{ helpers.formatBool(companySummary.useExtraNight) }}
            ({{ helpers.formatTimeSpan(companySummary.extraNightInit) }} → {{ helpers.formatTimeSpan(companySummary.extraNightEnd) }})
          </li>
          <li>
            <strong>{{ labels.companySettings.defaultTimeZone }}:</strong>
            {{ helpers.formatTimezoneFriendly(companySummary.defaultTimeZone, timezones) }}
          </li>
        </ul>
      </div>

      <!-- Form de aplicação -->
      <div class="box">
        <label class="box-title">Nova configuração HE3</label>

        <div class="form-row">
          <label>{{ labels.workShift.extraordinaryPhase }}</label>
          <select v-model.number="form.extraordinaryPhase" class="form-control">
            <option v-for="(label, key) in extraordinaryPhaseOptions" :key="key" :value="Number(key)">
              {{ label }}
            </option>
          </select>
        </div>

        <div class="form-row checkbox-line">
          <label>
            <input type="checkbox" v-model="form.useExtraNight" />
            {{ labels.companySettings.useExtraNight }}
          </label>
        </div>

        <div class="form-row" v-if="form.useExtraNight">
          <label>{{ labels.companySettings.extraNightInit }}</label>
          <input type="text" v-model="form.extraNightInit" class="form-control" placeholder="22:00:00" />
        </div>

        <div class="form-row" v-if="form.useExtraNight">
          <label>{{ labels.companySettings.extraNightEnd }}</label>
          <input type="text" v-model="form.extraNightEnd" class="form-control" placeholder="05:00:00" />
        </div>

        <div class="form-row checkbox-line">
          <label>
            <input type="checkbox" v-model="form.alsoApplyToCompanySettings" />
            Aplicar também na configuração da empresa
          </label>
        </div>
      </div>

      <!-- Turnos -->
      <div class="box">
        <label class="box-title">
          Turnos
          <span class="select-all">
            <input type="checkbox" :checked="allSelected" @change="toggleAll($event.target.checked)" />
            Selecionar todos
          </span>
        </label>

        <div class="workshift-search">
          <input type="text" v-model="workshiftFilter" placeholder="Buscar turno pelo nome..." />
          <span class="count">{{ filteredWorkshifts.length }} de {{ workshifts.length }} turno(s)</span>
        </div>

        <table class="table-workshifts">
          <thead>
            <tr>
              <th></th>
              <th>{{ labels.workShift.name }}</th>
              <th>{{ labels.workShift.workShiftType }}</th>
              <th>{{ labels.workShift.extraordinaryPhase }}</th>
              <th>{{ labels.workShift.useNightshiftPhase }}</th>
              <th>{{ labels.workShift.timeZone }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="ws in filteredWorkshifts" :key="ws.id">
              <td><input type="checkbox" :value="ws.id" v-model="selectedWorkshifts" /></td>
              <td>{{ ws.name }}</td>
              <td>{{ helpers.getWorkShiftTypeLabel(ws.workShiftType) }}</td>
              <td>{{ helpers.getExtraordinaryPhaseLabel(ws.extraordinaryPhase) }}</td>
              <td>{{ helpers.formatBool(ws.useNightshiftPhase) }}</td>
              <td>{{ helpers.formatInheritableTimezoneFriendly(ws.timeZone, companySummary.defaultTimeZone, timezones) }}</td>
              <td><a href="#" @click.prevent="openDetail(ws)">Detalhe</a></td>
            </tr>
            <tr v-if="!filteredWorkshifts.length">
              <td :colspan="7" class="empty-row">Nenhum turno encontrado para "{{ workshiftFilter }}".</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="actions">
        <button class="btn-cancel" @click="$router.go(-1)">Voltar</button>
        <button class="btn-primary" :disabled="!canSave || saving" @click="save()">
          <span v-if="!saving">Aplicar HE3</span>
          <span v-else>Aplicando...</span>
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import {
  GET_WORKSHIFT_FULL_VIEW,
  APPLY_HE3_BULK,
  GET_TIMEZONE_LIST
} from "../../../../urlConfig";
import { REQUEST_SERVICE } from "../../../../requestConfig";
import {
  workShiftLabels,
  companySettingsLabels,
  extraordinaryPhaseLabels,
  labelHelpers
} from "../../../../labels/workshiftLabels";
import modalWorkshiftDetail from "../../modals/modalWorkshiftDetail.vue";
import swal from "sweetalert";

export default {
  name: "configureHE3",
  components: { modalWorkshiftDetail },
  data() {
    return {
      loading: false,
      saving: false,
      companyId: "",
      companySummary: {},
      workshifts: [],
      selectedWorkshifts: [],
      workshiftFilter: "",
      detailWorkshift: null,
      // dicionário { "E. South America Standard Time": "(GMT -03:00) Brasília", ... }
      timezones: {},
      form: {
        extraordinaryPhase: 0,
        useExtraNight: false,
        extraNightInit: "22:00:00",
        extraNightEnd: "05:00:00",
        alsoApplyToCompanySettings: false
      },
      labels: {
        workShift: workShiftLabels,
        companySettings: companySettingsLabels
      },
      extraordinaryPhaseOptions: extraordinaryPhaseLabels,
      helpers: labelHelpers
    };
  },
  computed: {
    filteredWorkshifts() {
      const q = (this.workshiftFilter || "").trim().toLowerCase();
      if (!q) return this.workshifts;
      return this.workshifts.filter((w) => (w.name || "").toLowerCase().indexOf(q) !== -1);
    },
    allSelected() {
      const list = this.filteredWorkshifts;
      if (!list.length) return false;
      return list.every((w) => this.selectedWorkshifts.indexOf(w.id) !== -1);
    },
    canSave() {
      return this.selectedWorkshifts.length > 0 || this.form.alsoApplyToCompanySettings;
    }
  },
  beforeMount() {
    this.companyId = this.$route.params.idCompany;
    this.load();
  },
  methods: {
    load() {
      this.loading = true;

      // Carrega o dicionário de timezones em paralelo (global, sem context).
      REQUEST_SERVICE.get(GET_TIMEZONE_LIST)
        .then((r) => { this.timezones = r.data || {}; })
        .catch(() => { this.timezones = {}; });

      REQUEST_SERVICE.requestContext(
        "GET",
        `${GET_WORKSHIFT_FULL_VIEW}?companyId=${this.companyId}`,
        this.$route.params.context
      ).then((response) => {
          const data = response.data || {};
          this.companySummary = {
            defaultTimeZone: data.defaultTimeZone,
            applyRecalcPoints: data.applyRecalcPoints,
            extraordinaryPhase: data.extraordinaryPhase,
            useExtraNight: data.useExtraNight,
            extraNightInit: data.extraNightInit,
            extraNightEnd: data.extraNightEnd
          };
          this.workshifts = Array.isArray(data.workshifts) ? data.workshifts : [];
          this.form.extraordinaryPhase = data.extraordinaryPhase || 0;
          this.form.useExtraNight = !!data.useExtraNight;
          this.form.extraNightInit = data.extraNightInit || "22:00:00";
          this.form.extraNightEnd = data.extraNightEnd || "05:00:00";
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          swal("Aviso", "Falha ao carregar dados da empresa.", "error");
        });
    },
    toggleAll(checked) {
      const filteredIds = this.filteredWorkshifts.map((w) => w.id);
      if (checked) {
        const set = {};
        this.selectedWorkshifts.forEach((id) => { set[id] = true; });
        filteredIds.forEach((id) => { set[id] = true; });
        this.selectedWorkshifts = Object.keys(set);
      } else {
        const remove = {};
        filteredIds.forEach((id) => { remove[id] = true; });
        this.selectedWorkshifts = this.selectedWorkshifts.filter((id) => !remove[id]);
      }
    },
    openDetail(ws) {
      this.detailWorkshift = ws;
      $("#modalWorkshiftDetail").modal();
    },
    save() {
      if (this.saving || !this.canSave) return;
      this.saving = true;

      const payload = {
        companyId: this.companyId,
        workshiftIds: this.selectedWorkshifts,
        alsoApplyToCompanySettings: this.form.alsoApplyToCompanySettings,
        extraordinaryPhase: this.form.extraordinaryPhase,
        useExtraNight: this.form.useExtraNight,
        extraNightInit: this.form.extraNightInit,
        extraNightEnd: this.form.extraNightEnd
      };

      REQUEST_SERVICE.requestContext("PUT", APPLY_HE3_BULK, this.$route.params.context, payload)
        .then(() => {
          this.saving = false;
          swal("Sucesso", "HE3 aplicada em massa.", "success");
          this.load();
        })
        .catch(() => {
          this.saving = false;
          swal("Aviso", "Falha ao aplicar HE3.", "error");
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.configure-he3 {
  padding: 24px;

  .header {
    margin-bottom: 24px;
    h2 { font-weight: bold; margin-bottom: 8px; }
    p { color: #666; font-size: 13px; }
  }

  .loading-block { text-align: center; padding: 40px; color: #888; }

  .box {
    background: #fff; border-radius: 6px; padding: 18px;
    margin-bottom: 18px; box-shadow: 0 1px 3px rgba(0,0,0,0.06);

    .box-title {
      display: flex; justify-content: space-between; align-items: center;
      font-weight: 600; margin-bottom: 12px;
      .select-all { font-weight: normal; font-size: 13px; color: #555; input { margin-right: 4px; } }
    }
  }

  .summary-list { list-style: none; padding: 0; margin: 0; li { padding: 4px 0; font-size: 13px; } }

  .form-row {
    display: flex; flex-direction: column; gap: 4px; margin-bottom: 12px;
    label { font-weight: 500; font-size: 13px; }
    .form-control {
      border: 1px solid #ccc; border-radius: 4px; padding: 6px 10px;
      max-width: 320px;
    }
  }
  .checkbox-line label { flex-direction: row; align-items: center; gap: 6px; }

  .workshift-search {
    display: flex; align-items: center; gap: 10px; margin-bottom: 10px; flex-wrap: wrap;
    input {
      flex: 1 1 260px; min-width: 200px;
      border: 1px solid #ccc; border-radius: 4px; padding: 6px 10px; font-size: 13px;
    }
    .count { font-size: 12px; color: #888; }
  }
  .empty-row { text-align: center; color: #999; padding: 14px; font-style: italic; }

  .table-workshifts {
    width: 100%; border-collapse: collapse; font-size: 13px;
    th, td { border-bottom: 1px solid #eee; padding: 8px 6px; text-align: left; }
    th { color: #555; font-weight: 600; background: #fafafa; }
    a { color: #3598dc; }
  }

  .actions {
    display: flex; gap: 12px; justify-content: flex-end; margin-top: 20px;
    .btn-cancel, .btn-primary {
      border-radius: 4px; padding: 8px 24px; font-weight: 600; border: none; cursor: pointer;
    }
    .btn-cancel { background: #8b8787; color: #fff; }
    .btn-primary { background: #3598dc; color: #fff; }
    .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  }
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/restricted/companyDetail/configureHE3.vue